import { FileText } from 'lucide-react';

import { MemoizedIframePreview } from '@/components/treatment/v3/request/package-view/MemoizedIframePreview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetSubmissionAttemptDetailQuery } from '@/services/api/agentflowService';

import { DestinationSection } from './DestinationSection';
import { FieldsSection } from './FieldsSection';
import { PackageContentsSection } from './PackageContentsSection';
import { PipelineStepIndicator } from './PipelineStepIndicator';
import { SignatureSection } from './SignatureSection';
import { SubmissionAttemptActions } from './SubmissionAttemptActions';
import { SubmissionAttemptHeader } from './SubmissionAttemptHeader';
import {
  getSubmissionAttemptPresentationState,
  getSubmissionAttemptSummary,
} from './submissionAttemptPresentation';

function resolvePreviewUrl(pdfUrl: string | null): string | null {
  if (!pdfUrl) return null;
  try {
    return new URL(
      pdfUrl,
      import.meta.env.VITE_APP_DJANGO_BACKEND_HOST ?? window.location.origin,
    ).toString();
  } catch {
    return pdfUrl;
  }
}

interface SubmissionAttemptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenRequestModal: () => void;
  matterId: string;
  treatmentRequestId: string;
}

export const SubmissionAttemptModal = ({
  open,
  onOpenChange,
  onOpenRequestModal,
  matterId,
  treatmentRequestId,
}: SubmissionAttemptModalProps) => {
  const {
    data: detail,
    error,
    isLoading,
    refetch,
  } = useGetSubmissionAttemptDetailQuery(
    { matterId, requestId: treatmentRequestId },
    { skip: !open, refetchOnMountOrArgChange: true },
  );

  const previewUrl = resolvePreviewUrl(detail?.pdf_url ?? null);
  const pageCount = detail?.package_contents.reduce(
    (sum, c) => sum + (c.page_count ?? 0),
    0,
  );
  const hasError = Boolean(error) && !detail;
  const summary = detail ? getSubmissionAttemptSummary(detail) : null;
  const presentationState = detail
    ? getSubmissionAttemptPresentationState(detail)
    : null;
  const headerContent = (() => {
    if (detail) {
      return (
        <SubmissionAttemptHeader
          detail={detail}
          onClose={() => onOpenChange(false)}
        />
      );
    }

    if (hasError) {
      return (
        <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-6 py-3 shrink-0">
          <span className="text-sm font-medium text-zinc-900">
            Submission Attempt Unavailable
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-3 shrink-0">
        <Skeleton className="h-3 w-3 rounded-full" />
        <Skeleton className="h-5 w-48" />
      </div>
    );
  })();
  const leftPanelContent = (() => {
    if (hasError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-800">
            Failed to load submission attempt data.
          </p>
          <p className="mt-1 text-xs text-red-700">
            Retry, or open the manual submission flow if you only need
            to continue sending the request.
          </p>
        </div>
      );
    }

    if (isLoading || !detail) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      );
    }

    return (
      <>
        {detail.pipeline_steps.length > 0 && (
          <PipelineStepIndicator
            steps={detail.pipeline_steps}
            summaryTitle={summary?.title}
          />
        )}

        <DestinationSection
          destination={detail.destination}
          matterId={matterId}
          requestId={treatmentRequestId}
        />

        <SignatureSection
          signature={detail.signature}
          matterId={matterId}
          requestId={treatmentRequestId}
        />

        <PackageContentsSection
          packageContents={detail.package_contents}
        />

        <FieldsSection
          fields={detail.fields}
          matterId={matterId}
          requestId={treatmentRequestId}
        />
      </>
    );
  })();
  const previewPanel = (() => {
    if (hasError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-lg bg-zinc-100 px-6 text-center">
          <p className="text-sm font-medium text-zinc-700">
            Unable to load the submission attempt.
          </p>
          <p className="text-xs text-zinc-500">
            The backend has an attempt for this request, but this view did not
            load it successfully.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch();
            }}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (isLoading || !detail) {
      return <Skeleton className="flex-1 rounded-lg" />;
    }

    if (!previewUrl) {
      return (
        <div className="flex flex-1 items-center justify-center bg-zinc-100 rounded-lg">
          <span className="text-sm text-zinc-500">
            No PDF generated yet
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        <div className="flex items-center gap-2 mb-2 px-1 shrink-0">
          <FileText className="h-4 w-4 text-zinc-500" />
          <span className="text-sm font-medium text-zinc-700">
            Draft PDF
          </span>
          {pageCount != null && pageCount > 0 && (
            <span className="text-xs text-zinc-500">
              {pageCount} page{pageCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {presentationState?.reviewBlocked && summary?.title && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="text-xs font-medium text-amber-800">{summary.title}</p>
            {summary.description && (
              <p className="mt-1 text-xs leading-relaxed text-amber-700">
                {summary.description}
              </p>
            )}
          </div>
        )}
        <MemoizedIframePreview
          url={previewUrl}
          title="Draft PDF Preview"
          assumePdf
        />
      </div>
    );
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="flex h-[92vh] min-h-0 max-w-[96vw] flex-col p-0 overflow-hidden">
        {/* Header */}
        {headerContent}

        {/* Body: split panel */}
        <div className="flex flex-1 min-h-0">
          {/* Left panel */}
          <div className="flex w-[720px] shrink-0 flex-col border-r border-zinc-200">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {leftPanelContent}
            </div>

            {/* Actions footer */}
            {detail && (
              <SubmissionAttemptActions
                detail={detail}
                matterId={matterId}
                requestId={treatmentRequestId}
                onClose={() => onOpenChange(false)}
                onOpenRequestModal={onOpenRequestModal}
              />
            )}
          </div>

          {/* Right panel: PDF preview */}
          <div className="flex flex-1 flex-col min-h-0 min-w-0 p-3">
            {previewPanel}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
